import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';

import { EditableItemConfiguration, EditableItemType, MultiInput } from './editable-items';
import { NgForm } from '@angular/forms';
import { translationKeys } from '../../../core';
import { map, takeUntil } from 'rxjs/operators';
import { ReplaySubject } from 'rxjs';

@Component({
  selector: 'scip-multi-input',
  templateUrl: './multi-input.component.html',
  styleUrls: ['./multi-input.component.scss']
})
export class MultiInputComponent implements OnInit, OnDestroy {
  @Input() multiInputConfiguration: MultiInput;

  @Output() readonly selectionChanged: EventEmitter<EditableItemConfiguration> = new EventEmitter();

  @ViewChild('searchForm', { static: true }) searchForm: NgForm;

  editableItemType: typeof EditableItemType = EditableItemType;
  todayDate: Date;

  isDirty = false;

  translationKeys: typeof translationKeys = translationKeys;

  private destroyed$: ReplaySubject<boolean> = new ReplaySubject(1);

  constructor() {
    this.todayDate = new Date();
  }

  ngOnInit(): void {
    this.searchForm.statusChanges
      .pipe(
        map(() => {
          // Find the first control which has a change
          return Object.keys(this.searchForm.form.controls).find((key) => {
            const currentValue = this.searchForm.form.controls[key].value;
            if (currentValue) {
              const isEmptyInputText = currentValue === '';
              const isEmptyArray = Array.isArray(currentValue) && !currentValue.length;
              const isSelectedByDefaultRadioBtn = currentValue.isSelectedByDefault;
              return !!currentValue && !isEmptyInputText && !isSelectedByDefaultRadioBtn && !isEmptyArray;
            } else {
              return false; // null/empty ... means we have no element matching our search
            }
          });
        }),
        map((changedInput) => !!changedInput), // We have a control or undefined -> convert to boolean
        takeUntil(this.destroyed$)
      )
      .subscribe((isThereAnyChange) => {
        this.isDirty = isThereAnyChange;
      });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  clear(): void {
    this.multiInputConfiguration.multiInputConfigurations.forEach((el) => {
      el.configurableItems.forEach((e) => {
        if (e.type === EditableItemType.INPUT) {
          e.content = null;
        }
        // reset selection for autocomplete
        if (e.type === EditableItemType.AUTOCOMPLETE) {
          e.selection = [];
        }
        // reset selection for multiselect
        if (e.type === EditableItemType.MULTISELECT) {
          e.selection = [];
        }
        // reset to the default value for radiobuttons
        if (e.type === EditableItemType.RADIOBUTTONS) {
          e.inputs.forEach((radioButton) => {
            if (radioButton.isSelectedByDefault) e.content = radioButton;
          });
        }
        // reset to the default value for checkboxes
        if (e.type === EditableItemType.CHECKBOXES) {
          e.inputs.forEach((checkBox) => {
            if (checkBox.isSelectedByDefault) checkBox.selectedValue = true;
            else checkBox.selectedValue = false;
          });
        }
        // reset selection for 'from - to' date ranges
        if (e.type === EditableItemType.DATE_FROM_TO) {
          e.selection[0] = null;
          e.selection[1] = null;
        }
        // Emit all changes, because we do not know dependencies between selections.
        this.selectionChanged.emit(e);
      });
    });
    this.isDirty = false;
  }

  tryCallActionButtonAction(): void {
    if (this.multiInputConfiguration.actionButtonAction && this.multiInputConfiguration.actionButtonAction instanceof Function) {
      this.multiInputConfiguration.actionButtonAction(!this.isDirty);
    }
  }

  itemSelectionChanged(sourceItem: EditableItemConfiguration): void {
    this.selectionChanged.emit(sourceItem);
  }
}
